import { announce } from "@kovernow/flows/announce";
import { CREATE_ERROR_FIELDS } from "@kovernow/flows/add_item";
import { ApiError, toRecoveryAction, type RecoveryAction } from "@kovernow/flows/recovery";
import {
  UPLOAD_LIMITS,
  UploadsUnavailableError,
  type QueuedFile,
  type UploadKind,
  type UploadQueue,
} from "@kovernow/flows/uploads";
import type { UploadFieldRow } from "@kovernow/react-ui/components/upload-field";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";

function recoveryFor(err: unknown): RecoveryAction {
  if (err instanceof UploadsUnavailableError) {
    return {
      errorCode: "uploads_unavailable",
      action: "retry",
      kind: "recoverable",
      messageKey: "errors.uploads_unavailable",
    };
  }
  return toRecoveryAction(err);
}

/** Touch device ("Take photo") and camera permission (denied → one line). */
function useCamera(): { canCapture: boolean; denied: boolean } {
  const [state, setState] = useState(() => ({
    canCapture: typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches,
    denied: false,
  }));
  useEffect(() => {
    if (!state.canCapture) return;
    let cancelled = false;
    try {
      void navigator.permissions
        ?.query({ name: "camera" as PermissionName })
        .then((p) => {
          if (!cancelled) setState((s) => ({ ...s, denied: p.state === "denied" }));
        })
        .catch(() => undefined);
    } catch {
      // Permissions API unsupported for camera: keep Take photo.
    }
    return () => {
      cancelled = true;
    };
  }, [state.canCapture]);
  return state;
}

export type FieldErrorTarget = (typeof CREATE_ERROR_FIELDS)[string];

/**
 * The upload → `POST /items` pipeline shared by the legacy photos step and
 * the collector documents step. Nothing uploads until `submit`; the queue
 * uploads 3 at a time, and `createItem` runs only when every file is done.
 * A failed file gets its own Retry; a token/config failure or a failed POST
 * shows a RecoveryView; a code in `CREATE_ERROR_FIELDS` goes to its field.
 * Uploaded files are kept, so a resubmit never uploads them again.
 */
export function useUploadPipeline({
  queue,
  ownerId,
  inFlight,
  createItem,
  onFieldError,
  errorFields = CREATE_ERROR_FIELDS,
  onApiError,
}: {
  queue: UploadQueue;
  ownerId: string | null;
  /** Owned by AddItem: browser Back/Forward remounts the step mid-save. */
  inFlight: { current: boolean };
  /** Builds the payload from `queue.documents()` and posts it. Throws on failure. */
  createItem: () => Promise<void>;
  onFieldError: (target: FieldErrorTarget, messageKey: string) => void;
  /** Codes that point at a field (W-6 edit passes `UPDATE_ERROR_FIELDS`). */
  errorFields?: Record<string, FieldErrorTarget>;
  /** Handles a failure itself (e.g. `item_has_policy`); return true to skip the RecoveryView. */
  onApiError?: (err: unknown) => boolean;
}) {
  const t = useT();
  const { format } = useServices();
  const qs = useSyncExternalStore(queue.subscribe, queue.getState);
  const camera = useCamera();
  const [busy, setBusy] = useState<"uploading" | "saving" | null>(null);
  const [recovery, setRecovery] = useState<RecoveryAction | null>(null);
  const [uploadsFailed, setUploadsFailed] = useState(false);

  // Done and failed are announced politely, once per transition.
  const lastStatus = useRef(new Map<string, QueuedFile["status"]>());
  useEffect(() => {
    const prev = lastStatus.current;
    const next = new Map<string, QueuedFile["status"]>();
    for (const f of qs.files) {
      next.set(f.id, f.status);
      if (prev.get(f.id) === f.status) continue;
      if (f.status === "done") announce(t("uploads.done", { name: f.name }));
      if (f.status === "failed") announce(t("uploads.failed", { name: f.name }));
    }
    lastStatus.current = next;
  }, [qs.files, t]);

  const create = async () => {
    setBusy("saving");
    try {
      await createItem();
    } catch (err) {
      const code = err instanceof ApiError ? err.problem?.errorCode : undefined;
      const mapped = code ? errorFields[code] : undefined;
      if (mapped) onFieldError(mapped, `errors.${code}`);
      else if (!onApiError?.(err)) setRecovery(toRecoveryAction(err));
    } finally {
      setBusy(null);
    }
  };

  const run = async (upload: () => Promise<boolean>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRecovery(null);
    setUploadsFailed(false);
    try {
      setBusy("uploading");
      const ok = await upload();
      if (!ok) {
        const err = queue.lastError();
        if (err) setRecovery(recoveryFor(err));
        else setUploadsFailed(queue.getState().files.some((f) => f.status === "failed"));
        setBusy(null);
        return;
      }
      await create();
    } finally {
      inFlight.current = false;
    }
  };

  const rowsFor = (kind: UploadKind): UploadFieldRow[] =>
    qs.files
      .filter((f) => f.kind === kind)
      .map((f) => {
        const progress = t("uploads.progress", { name: f.name, n: format.number(f.progress) });
        const statusText: Record<QueuedFile["status"], string> = {
          preparing: t("uploads.preparing", { name: f.name }),
          ready: t("uploads.ready", { name: f.name }),
          uploading: progress,
          done: t("uploads.done", { name: f.name }),
          failed: t("uploads.failed", { name: f.name }),
          invalid: "",
        };
        const errorText =
          f.status === "invalid" && f.error
            ? f.error === "heicFailed"
              ? t("uploads.heicFailed", { name: f.name })
              : t(`uploads.${f.error}`)
            : undefined;
        return {
          id: f.id,
          name: f.name,
          status: f.status,
          progress: f.progress,
          statusText: statusText[f.status],
          progressLabel: f.status === "uploading" ? progress : undefined,
          errorText,
          removeLabel: t("uploads.remove", { name: f.name }),
          retryLabel: f.status === "failed" ? t("uploads.retry", { name: f.name }) : undefined,
        };
      });

  /** Files dropped on the last add: over the kind's max, or over the total. */
  const limitError = (kind: UploadKind): string | undefined =>
    qs.tooMany[kind]
      ? t("uploads.tooMany", { max: format.number(UPLOAD_LIMITS[kind].max) })
      : qs.overTotal[kind]
        ? t("uploads.limits.total")
        : undefined;

  const locked = busy !== null;
  const common = {
    chooseFileLabel: t("uploads.chooseFile"),
    takePhotoLabel: t("uploads.takePhoto"),
    canCapture: camera.canCapture,
    cameraDeniedText: camera.denied ? t("uploads.cameraDenied") : undefined,
    locked,
    onRemove: (id: string) => queue.remove(id),
    onRetry: (id: string) => void run(() => queue.retry(id, ownerId)),
  };

  return { qs, busy, locked, recovery, uploadsFailed, run, rowsFor, limitError, common };
}

/** Under the fields: the failed-uploads line, the RecoveryView and the polite progress line. */
export function PipelineStatus({
  uploadsFailed,
  recovery,
  onRetry,
}: {
  uploadsFailed: boolean;
  recovery: RecoveryAction | null;
  onRetry: () => void;
}) {
  const t = useT();
  return (
    <>
      {uploadsFailed ? (
        <p className="text-sm font-semibold text-status-error" data-testid="uploads-failed">
          {t("addItem.errors.uploadsFailed")}
        </p>
      ) : null}
      {recovery ? (
        <div data-testid="add-item-error">
          <Recovery action={recovery} onRetry={onRetry} />
        </div>
      ) : null}
    </>
  );
}

export function BusyLine({ busy }: { busy: "uploading" | "saving" | null }) {
  const t = useT();
  return (
    <p role="status" className="text-sm text-muted-foreground">
      {busy === "uploading" ? t("addItem.uploading") : busy === "saving" ? t("addItem.saving") : ""}
    </p>
  );
}
